import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { toDay, toLocal } from "@/lib/time";

export function usePlanner(weekStart, viewUserId) {
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const ws = toDay(weekStart);
  const params = viewUserId ? { user_id: viewUserId } : {};

  const load = useCallback(async () => {
    try {
      const [t, e, a, av] = await Promise.all([
        api.get("/tasks", { params }),
        api.get("/events", { params: { ...params, week_start: ws } }),
        api.get("/ai/latest", { params: { ...params, week_start: ws } }),
        api.get("/availability", { params: { ...params, start: ws, days: 7 } }),
      ]);
      setTasks(t.data.filter((x) => x.in_planner !== false));
      setEvents(e.data);
      setInsight(a.data);
      setOverrides(av.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, viewUserId]);

  const replan = useCallback(async (silent = true) => {
    const { data } = await api.post("/schedule/auto", { week_start: ws, now: toLocal(new Date()), user_id: viewUserId || null });
    await load();
    if (!silent) {
      toast.success(`Planned ${data.scheduled} task${data.scheduled === 1 ? "" : "s"} this week`, {
        description: data.at_risk ? `${data.at_risk} need attention (deadline risk or not enough time).` : "Everything fits before its deadline.",
      });
    }
    return data;
  }, [ws, viewUserId, load]);

  useEffect(() => {
    setLoading(true);
    replan().catch(() => load());
  }, [replan, load]);

  return { tasks, events, overrides, insight, setInsight, loading, replan, ws };
}
